import { type FormEvent, useState } from "react";

import { Button } from "@/components/ui";
import { useAuthStore } from "@/stores/authStore";

import { AuthError } from "./AuthError";

/** Email and password. Owners, managers and platform staff, from the back office. */
export function PasswordLoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const login = useAuthStore((s) => s.loginWithPassword);
  const error = useAuthStore((s) => s.error);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    try {
      await login(email.trim(), password);
    } catch {
      // The store holds the message. The password is deliberately NOT cleared:
      // a typo in the email should not cost the user their password too.
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
      <div>
        <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-ink-900">
          Email address
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="username"
          required
          className="h-touch-lg w-full rounded-control border border-line bg-surface px-3 text-sm text-ink-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
        />
      </div>

      <div>
        <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-ink-900">
          Password
        </label>
        <input
          id="password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
          className="h-touch-lg w-full rounded-control border border-line bg-surface px-3 text-sm text-ink-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
        />
      </div>

      <AuthError message={error} />

      <Button
        type="submit"
        variant="primary"
        size="pos"
        fullWidth
        loading={busy}
        disabled={!email.trim() || !password}
      >
        Sign in
      </Button>
    </form>
  );
}
